import React, { useEffect, useRef, useState } from 'react';
import './style.scss';
import { AnimatedHeading, HoverText } from '../../shared/TextFx';

const AdvantagesSec = () => {
  const introRef = useRef(null);
  const [introVisible, setIntroVisible] = useState(false);

  // Start the intro sequence once the block is well inside the viewport.
  useEffect(() => {
    const node = introRef.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setIntroVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIntroVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const delay = (s) => ({ '--adv-delay': `${s}s` });

  return (
    <section className="esq-advantage-sec" id="advantages">
      <div className="wrap">
        <div className="adv-sticky reveal">
          <AnimatedHeading className="h-display" text="Industries" />

          <div
            ref={introRef}
            className={`adv-intro js-anim${introVisible ? ' is-in' : ''}`}
          >
            <h6 className="adv-subtitle adv-anim" style={delay(0)}>
              <span className="adv-subtitle-text">The cost of waiting</span>
              <span className="adv-subtitle-bar" aria-hidden="true" />
            </h6>

            <h4 className="adv-sub-heading adv-anim" style={delay(0.12)}>
              When delay costs more than delivery.
            </h4>

            <p className="adv-para-desc adv-para-desc--lead adv-anim" style={delay(0.24)}>
              Sometimes one shipment determines what happens next.
            </p>

            <p className="adv-para-desc adv-anim" style={delay(0.36)}>
              An aircraft is waiting for a part. A production line is stopped by
              one component. A semiconductor facility is working against a
              production deadline. A collection needs to reach its launch. A
              system needs to come back online.
            </p>

            <p className="adv-para-desc adv-para-desc--closing adv-anim" style={delay(0.48)}>
              When the cost of waiting exceeds the cost of moving, standard
              logistics is no longer enough.
            </p>

            <div className="adv-cta adv-anim" style={delay(0.6)}>
              <div className="adv-cta adv-anim" style={delay(0.6)}>
                <a href="/industries" className="btn _accent text-hover">
                  <span className="btn__inner">
                    <HoverText words="Explore Industries" />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="adv-list">
          <div className="adv-item reveal">
            <h3>Aerospace & Aviation</h3>
            <p>Critical parts moved against aircraft return to service deadlines.</p>
          </div>

          <div className="adv-item reveal">
            <h3>Automotive & Manufacturing</h3>
            <p>Components and equipment moved urgently when production cannot afford to stop.</p>
          </div>

          <div className="adv-item reveal">
            <h3>Semiconductors</h3>
            <p>Time sensitive parts and equipment moved when production schedules leave no room for delay.</p>
          </div>

          <div className="adv-item reveal">
            <h3>Textiles & Garments</h3>
            <p>Samples, garments and accessories moved against runway, launch and production deadlines.</p>
          </div>

          <div className="adv-item reveal">
            <h3>IT Infrastructure</h3>
            <p>Critical servers, network equipment and replacement components moved when infrastructure uptime is at stake.</p>
          </div>

          <div className="adv-item reveal">
            <h3>Sensitive Documents</h3>
            <p>Time sensitive originals and essential documents delivered against deadlines that cannot move.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSec;
